// Basis-Interfaces für Fingerprint-Komponenten
interface BasicInfo {
  userAgent: string;
  platform: string;
  cookieEnabled: boolean;
  doNotTrack: string | null;
  maxTouchPoints: number;
  devicePixelRatio: number;
}

interface ScreenInfo {
  width: number;
  height: number;
  availWidth: number;
  availHeight: number;
  colorDepth: number;
  pixelDepth: number;
  innerWidth: number;
  innerHeight: number;
}

interface WebGLInfo {
  vendor: string;
  renderer: string;
  version: string;
  shadingLanguageVersion: string;
  maxTextureSize: number;
  maxViewportDims: Int32Array;
  maxRenderBufferSize: number;
}

interface AudioInfo {
  sampleRate: number;
  state: AudioContextState;
  maxChannelCount: number;
  channelCount: number;
}

interface HardwareInfo {
  cores: number | 'unknown';
  memory: number | 'unknown';
  connection: ConnectionInfo | 'unknown';
}

interface ConnectionInfo {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

interface TimezoneInfo {
  timezone: string;
  offset: number;
  isDST: boolean;
}

interface LanguageInfo {
  primary: string;
  languages: string[];
}

interface FingerprintComponents {
  basic: string;
  screen: string;
  canvas: string;
  webgl: string;
  audio: string;
  fonts: string;
  hardware: string;
  timezone: string;
  language: string;
  plugins: string;
}

interface FingerprintResult {
  hash: string;
  components: FingerprintComponents;
  raw: string;
  metadata: {
    timestamp: number;
    userAgent: string;
    version: string;
  };
}

interface ExtendedNavigator extends Navigator {
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

export class BrowserFingerprint {
  private readonly version: string = '2.0.0';
  private fingerprint: Partial<FingerprintComponents> = {};

  constructor() {
    // Initialisierung falls nötig
  }

  /**
   * Hauptfunktion zum Erstellen des Browser-Fingerprints
   * @returns Promise mit vollständigem Fingerprint-Objekt
   */
  public async generateFingerprint(): Promise<FingerprintResult> {
    try {
      const components = await Promise.all([
        this.getBasicInfo(),
        this.getScreenInfo(),
        this.getCanvasFingerprint(),
        this.getWebGLFingerprint(),
        this.getAudioFingerprint(),
        this.getFontsFingerprint(),
        this.getHardwareInfo(),
        this.getTimezoneInfo(),
        this.getLanguageInfo(),
        this.getPluginsInfo(),
      ]);

      const combinedData = components.join('|');
      const fingerprintHash = await this.hashString(combinedData);

      return {
        hash: fingerprintHash,
        components: {
          basic: components[0],
          screen: components[1],
          canvas: components[2],
          webgl: components[3],
          audio: components[4],
          fonts: components[5],
          hardware: components[6],
          timezone: components[7],
          language: components[8],
          plugins: components[9],
        },
        raw: combinedData,
        metadata: {
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
          version: this.version,
        },
      };
    } catch (error) {
      throw new Error(`Fingerprint generation failed: ${error}`);
    }
  }

  /**
   * Sammelt grundlegende Browser-Informationen
   * @returns Serialisierte Basic-Info als String
   */
  private getBasicInfo(): string {
    const info: BasicInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      devicePixelRatio: window.devicePixelRatio || 1,
    };

    return [
      info.userAgent,
      info.platform,
      info.cookieEnabled,
      info.doNotTrack,
      info.maxTouchPoints,
      info.devicePixelRatio,
    ].join(',');
  }

  /**
   * Sammelt Bildschirm- und Viewport-Informationen
   * @returns Serialisierte Screen-Info als String
   */
  private getScreenInfo(): string {
    const info: ScreenInfo = {
      width: screen.width,
      height: screen.height,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
    };

    return [
      info.width,
      info.height,
      info.availWidth,
      info.availHeight,
      info.colorDepth,
      info.pixelDepth,
      info.innerWidth,
      info.innerHeight,
    ].join(',');
  }

  /**
   * Erstellt Canvas-basiertes Fingerprint
   * @returns Canvas-Daten als Base64 String oder Fehlermeldung
   */
  private getCanvasFingerprint(): string {
    try {
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');

      if (!ctx) {
        return 'canvas_context_not_available';
      }

      canvas.width = 200;
      canvas.height = 50;

      // Text mit verschiedenen Eigenschaften zeichnen
      ctx.textBaseline = 'top';
      ctx.font = "14px 'Arial'";
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);

      ctx.fillStyle = '#069';
      ctx.fillText('BrowserFingerprint 🔍', 2, 15);

      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Canvas Test', 4, 30);

      // Geometrische Formen hinzufügen
      ctx.beginPath();
      ctx.arc(50, 25, 20, 0, Math.PI * 2);
      ctx.stroke();

      return canvas.toDataURL();
    } catch (error) {
      return `canvas_error: ${error}`;
    }
  }

  /**
   * Erstellt WebGL-basiertes Fingerprint
   * @returns Serialisierte WebGL-Info oder Fehlermeldung
   */
  private getWebGLFingerprint(): string {
    try {
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const gl: WebGLRenderingContext | null = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

      if (!gl) {
        return 'webgl_not_supported';
      }

      const info: WebGLInfo = {
        vendor: gl.getParameter(gl.VENDOR) || 'unknown',
        renderer: gl.getParameter(gl.RENDERER) || 'unknown',
        version: gl.getParameter(gl.VERSION) || 'unknown',
        shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION) || 'unknown',
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0,
        maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS) || new Int32Array([0, 0]),
        maxRenderBufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || 0,
      };

      return JSON.stringify(info);
    } catch (error) {
      return `webgl_error: ${error}`;
    }
  }

  /**
   * Erstellt Audio-Context-basiertes Fingerprint
   * @returns Promise mit serialisierten Audio-Info
   */
  private async getAudioFingerprint(): Promise<string> {
    try {
      const AudioContextClass: typeof AudioContext = window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) {
        return 'audio_not_supported';
      }

      const audioCtx: AudioContext = new AudioContextClass();

      const oscillator: OscillatorNode = audioCtx.createOscillator();
      const analyser: AnalyserNode = audioCtx.createAnalyser();
      const gainNode: GainNode = audioCtx.createGain();

      oscillator.connect(analyser);
      analyser.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.frequency.value = 1000;
      oscillator.type = 'triangle';

      const freqData = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(freqData);

      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.1);

      const audioInfo: AudioInfo = {
        sampleRate: audioCtx.sampleRate,
        state: audioCtx.state,
        maxChannelCount: audioCtx.destination.maxChannelCount,
        channelCount: audioCtx.destination.channelCount,
      };

      await audioCtx.close();
      return JSON.stringify(audioInfo);
    } catch (error) {
      return `audio_error: ${error}`;
    }
  }

  /**
   * Erkennt verfügbare Schriftarten
   * @returns Komma-separierte Liste erkannter Fonts
   */
  private getFontsFingerprint(): string {
    const baseFonts: string[] = ['monospace', 'sans-serif', 'serif'];
    const testFonts: string[] = [
      'Arial',
      'Arial Black',
      'Comic Sans MS',
      'Courier New',
      'Georgia',
      'Helvetica',
      'Impact',
      'Times New Roman',
      'Trebuchet MS',
      'Verdana',
      'Calibri',
      'Cambria',
      'Consolas',
      'Segoe UI',
      'Roboto',
      'Open Sans',
    ];

    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

    if (!context) {
      return 'font_detection_failed';
    }

    const baselines: Record<string, number> = {};
    baseFonts.forEach((font: string) => {
      context.font = `${testSize} ${font}`;
      baselines[font] = context.measureText(testString).width;
    });

    const detectedFonts: string[] = [];
    testFonts.forEach((font: string) => {
      let detected = false;
      baseFonts.forEach((baseFont: string) => {
        context.font = `${testSize} ${font}, ${baseFont}`;
        const width: number = context.measureText(testString).width;
        if (width !== baselines[baseFont]) {
          detected = true;
        }
      });
      if (detected) {
        detectedFonts.push(font);
      }
    });

    return detectedFonts.join(',');
  }

  /**
   * Sammelt Hardware-Informationen
   * @returns Serialisierte Hardware-Info
   */
  private getHardwareInfo(): string {
    const nav = navigator as ExtendedNavigator;

    const info: HardwareInfo = {
      cores: nav.hardwareConcurrency || 'unknown',
      memory: nav.deviceMemory || 'unknown',
      connection: nav.connection
        ? {
            effectiveType: nav.connection.effectiveType,
            downlink: nav.connection.downlink,
            rtt: nav.connection.rtt,
          }
        : 'unknown',
    };

    return JSON.stringify(info);
  }

  /**
   * Sammelt Zeitzone-Informationen
   * @returns Serialisierte Timezone-Info
   */
  private getTimezoneInfo(): string {
    const timezoneInfo: TimezoneInfo = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: new Date().getTimezoneOffset(),
      isDST: this.isDST(),
    };

    return [timezoneInfo.timezone, timezoneInfo.offset, timezoneInfo.isDST].join(',');
  }

  /**
   * Prüft ob Sommerzeit aktiv ist
   * @returns Boolean für DST-Status
   */
  private isDST(): boolean {
    const jan: number = new Date(new Date().getFullYear(), 0, 1).getTimezoneOffset();
    const jul: number = new Date(new Date().getFullYear(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== new Date().getTimezoneOffset();
  }

  /**
   * Sammelt Sprach-Informationen
   * @returns Serialisierte Language-Info
   */
  private getLanguageInfo(): string {
    const languageInfo: LanguageInfo = {
      primary: navigator.language,
      languages: navigator.languages ? Array.from(navigator.languages) : [],
    };

    return [languageInfo.primary, languageInfo.languages.join(',')].join('|');
  }

  /**
   * Sammelt Plugin-Informationen
   * @returns Serialisierte Plugin-Info
   */
  private getPluginsInfo(): string {
    if (!navigator.plugins) {
      return 'no_plugins';
    }

    const plugins: string[] = Array.from(navigator.plugins)
      .map((plugin: Plugin) => plugin.name)
      .sort();

    return plugins.join(',');
  }

  /**
   * Erstellt SHA-256 Hash oder Fallback
   * @param str - Zu hashender String
   * @returns Promise mit Hash-String
   */
  private async hashString(str: string): Promise<string> {
    if (!window.crypto || !window.crypto.subtle) {
      return this.simpleHash(str);
    }

    try {
      const encoder = new TextEncoder();
      const data: Uint8Array = encoder.encode(str);
      const hashBuffer: ArrayBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray: number[] = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b: number) => b.toString(16).padStart(2, '0')).join('');
    } catch (error: unknown) {
      return this.simpleHash(str);
    }
  }

  /**
   * Einfache Hash-Funktion als Fallback
   * @param str - Zu hashender String
   * @returns Hash als Hex-String
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char: number = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // 32-bit Integer
    }
    return Math.abs(hash).toString(16);
  }
}
